import { formatPriceWithAssetDecimals } from '../utils/priceFormatter';
import { subscribeCandles } from './candleFeed';
import liveChartStream from './liveChartStream';
import {
  Candle,
  VidyaHeartParams,
  ReversalSetup,
  computeHeartSeries,
  detectReversalSetup,
  heartIntervalFromConfig,
  heartParamsFromConfig,
  heartSide,
  requiredCandles,
  setupTriggered
} from './vidyaHeart';

export interface AISymbolData {
  symbol: string;
  price: string;
  lastUpdate: number;
  priceHistory?: Array<{price: number, timestamp: number}>;
  strategy?: string;
  config?: any;
  lastSignalTime?: number;
}

/**
 * HUGE♥HUG is registered under the id `supertrend` in the settings screen, which is a
 * leftover from the strategy it replaced. The id is what reaches this service and what is
 * stored in Firestore against existing users, so it is matched here rather than renamed.
 */
const HEART_STRATEGY_IDS = new Set(['supertrend', 'vidyaheart', 'hugehug']);

/** Pause between a position closing and the strategy being allowed to re-enter. */
const HEART_REENTRY_COOLDOWN_MS = 30000;

/**
 * How long to wait after asking for a trade before asking again. Opening a position is
 * asynchronous, so without this the entry check would fire repeatedly in the window before
 * the new position appears in the open list.
 */
const HEART_ENTRY_RETRY_MS = 5000;

export interface AITradingServiceConfig {
  onSignal: (symbol: string, signal: 'buy' | 'sell', price: string) => void;
  onPriceUpdate: (symbol: string, price: string) => void;
}

interface HeartFeed {
  unsubscribe: () => void;
  /** Trade stream, watched so an armed setup triggers the moment its level is taken out. */
  unsubscribeTrades: () => void;
  interval: string;
  params: VidyaHeartParams;
  /** Bar time of the last close that was evaluated, so a replayed bar cannot fire twice. */
  lastBarTime: number;
  ready: boolean;
  candleCount: number;
  status: HeartStatus | null;
  /** Whether an AI position for this symbol is currently open, as reported by the context. */
  positionOpen: boolean;
  /** No entry before this time; set when a position closes. */
  cooldownUntil: number;
  /** When the last entry was requested, for the retry guard. */
  lastEntryRequest: number;
  /**
   * The three-candle reversal armed by the bar that just closed, if any, waiting for its
   * level to be taken out. Replaced on every close, so a setup that is not triggered during
   * the bar that follows it expires rather than firing later at a price it never validated.
   */
  setup: ReversalSetup | null;
  /** The heart's colour as of the previous close, used to notice it changing. */
  lastTrendUp: boolean | null;
  /**
   * Set when the heart changes colour and cleared by the next entry. The trade that takes a
   * freshly flipped heart skips the reversal pattern; everything after it needs one.
   */
  flipEntryPending: boolean;
}

/** What the strategy is currently looking at, for display in the settings panel. */
export interface HeartStatus {
  interval: string;
  /** The heart's price. Null until the trend has been established. */
  level: number | null;
  /** True while the heart is green and below price, false while it is red and above. */
  isTrendUp: boolean | null;
  close: number;
  barTime: number;
  candleCount: number;
  requiredCandles: number;
}

class AITradingService {
  private activeSymbols: Set<string> = new Set();
  private symbolData: Map<string, AISymbolData> = new Map();
  private intervals: Map<string, NodeJS.Timeout> = new Map();
  private startupDelays: Map<string, NodeJS.Timeout> = new Map();
  private heartFeeds: Map<string, HeartFeed> = new Map();
  private config: AITradingServiceConfig | null = null;
  private isRunning = false;

  // Testing mode - set to true for very frequent signals
  private readonly TESTING_MODE = true;
  
  // AI startup delay - 1 minute before trading begins
  private readonly AI_STARTUP_DELAY = 20000; // 60 seconds = 1 minute

  constructor() {
    // Deliberately does not start the monitoring interval. This is a module-level
    // singleton, so starting a timer here meant a 2-second interval ran from the moment
    // the bundle loaded — before login, and forever after, whether or not AI trading was
    // ever used. Monitoring now starts with the first symbol and stops with the last.
  }

  setConfig(config: AITradingServiceConfig) {
    this.config = config;
  }

  addSymbol(symbol: string, strategy: string = 'supertrend', config: any = {}) {
    this.activeSymbols.add(symbol);
    this.startGlobalPriceMonitoring();
    // Store strategy and config for this symbol
    this.symbolData.set(symbol, {
      symbol,
      price: '0',
      lastUpdate: 0,
      strategy,
      config
    });

    // HUGE♥HUG acts on candle closes, so it runs off the kline stream rather than the
    // polling loop the tick strategies use.
    if (HEART_STRATEGY_IDS.has(strategy)) {
      this.startHeartFeed(symbol, config);
    }

    // Start AI trading with 1-minute delay
    this.startSymbolMonitoringWithDelay(symbol);
  }

  removeSymbol(symbol: string) {
    this.activeSymbols.delete(symbol);
    
    // Stop monitoring this symbol
    this.stopSymbolMonitoring(symbol);
    
    // Clear any pending startup delay
    this.clearStartupDelay(symbol);

    this.stopHeartFeed(symbol);

    // Nothing left to monitor, so stop the shared interval rather than leaving it running.
    if (this.activeSymbols.size === 0) {
      this.stopGlobalPriceMonitoring();
    }
  }

  updateSymbolPrice(symbol: string, price: string) {
    // Get existing data to preserve priceHistory and other properties
    const existingData = this.symbolData.get(symbol);
    
    this.symbolData.set(symbol, {
      ...existingData, // Preserve existing data including priceHistory
      symbol,
      price,
      lastUpdate: Date.now()
    });

    // Notify about price update
    if (this.config) {
      this.config.onPriceUpdate(symbol, price);
    }
  }

  private globalMonitoringInterval: NodeJS.Timeout | null = null;

  private startGlobalPriceMonitoring() {
    if (this.isRunning) return;
    
    this.isRunning = true;
   
    // Monitor all active symbols every 2 seconds
    this.globalMonitoringInterval = setInterval(() => {
      this.monitorAllSymbols();
    }, 2000);
  }

  private stopGlobalPriceMonitoring() {
    if (this.globalMonitoringInterval) {
      clearInterval(this.globalMonitoringInterval);
      this.globalMonitoringInterval = null;
    }
    this.isRunning = false;
  }

  private startSymbolMonitoring(symbol: string) {
    // Clear any existing interval for this symbol
    this.stopSymbolMonitoring(symbol);    
    // Start AI signal generation for this symbol
    const intervalTime = this.TESTING_MODE ? 1000 : 2000; // 1 second for testing, 2 seconds for normal
    const interval = setInterval(() => {
      this.generateAISignal(symbol);
    }, intervalTime);

    this.intervals.set(symbol, interval);
  }

  private stopSymbolMonitoring(symbol: string) {
    const interval = this.intervals.get(symbol);
    if (interval) {
      clearInterval(interval);
      this.intervals.delete(symbol);
    }
  }

  private startSymbolMonitoringWithDelay(symbol: string) {
    // Clear any existing startup delay for this symbol
    this.clearStartupDelay(symbol);
    
    // console.log(`🤖 AI Trading: Starting ${symbol} with 1-minute initialization delay...`);
    
    // Set up 1-minute delay before starting actual trading
    const startupDelay = setTimeout(() => {
      // console.log(`✅ AI Trading: ${symbol} initialization complete - starting trade execution`);
      this.startSymbolMonitoring(symbol);
      this.startupDelays.delete(symbol);
    }, this.AI_STARTUP_DELAY);
    
    this.startupDelays.set(symbol, startupDelay);
  }

  private clearStartupDelay(symbol: string) {
    const delay = this.startupDelays.get(symbol);
    if (delay) {
      clearTimeout(delay);
      this.startupDelays.delete(symbol);
      // console.log(`⏹️ AI Trading: Startup delay cleared for ${symbol}`);
    }
  }

  /**
   * Watch closed bars on the configured entry timeframe and act on the heart flipping side.
   *
   * The whole series is recomputed on each close rather than carried between calls. The
   * trend is a latch that can only change on a crossing, so recomputing it from the same
   * window always yields the same state, and holding no state across calls means a
   * reconnection or a replayed bar cannot leave the latch out of step with the data.
   */
  private startHeartFeed(symbol: string, config: any) {
    this.stopHeartFeed(symbol);

    const params = heartParamsFromConfig(config);
    const interval = heartIntervalFromConfig(config);
    // Comfortably more than the warm-up rather than exactly it. The window slides as bars
    // arrive, and the trend is a latch built by replaying the window from its start, so
    // the oldest bars need to be old enough that the recent crossings are always inside it.
    const windowSize = Math.min(Math.max(requiredCandles(params) + 200, 500), 1000);

    const feed: HeartFeed = {
      unsubscribe: () => {},
      unsubscribeTrades: () => {},
      interval,
      params,
      lastBarTime: 0,
      ready: false,
      candleCount: 0,
      status: null,
      positionOpen: false,
      cooldownUntil: 0,
      lastEntryRequest: 0,
      setup: null,
      lastTrendUp: null,
      flipEntryPending: false
    };
    this.heartFeeds.set(symbol, feed);

    feed.unsubscribe = subscribeCandles(symbol, interval, windowSize, {
      onReady: candles => {
        const current = this.heartFeeds.get(symbol);
        if (!current) return;
        current.ready = true;
        // Whatever the history ends on is the starting point, not a signal: acting on it
        // would open a position purely because the page had just loaded.
        current.lastBarTime = candles.length > 0 ? candles[candles.length - 1].time : 0;
        this.refreshHeartStatus(symbol, candles);
        // Seeded from the history so the first live close is compared against the colour the
        // heart already had, rather than reading as a change of colour.
        current.lastTrendUp = current.status?.isTrendUp ?? null;
      },
      onClosedBar: candles => this.evaluateHeart(symbol, candles),
      onError: error => {
        console.warn(`AI trading: no candle history for ${symbol} ${interval}`, error);
      }
    });

    // An armed setup waits on a price, not on a bar, so it is watched trade by trade. The
    // 2s monitoring tick would also catch it eventually, but only after the level had been
    // through — on a fast bar that is a materially worse fill, and a level that is only
    // nicked between two ticks would be missed altogether. The comparison per trade is one
    // number against another, and `liveChartStream` reference counts its sockets, so this
    // shares the connection the candles already use.
    feed.unsubscribeTrades = liveChartStream.subscribe(symbol, interval, {
      onTrade: trade => {
        const current = this.heartFeeds.get(symbol);
        if (!current?.setup) return;
        if (!setupTriggered(current.setup, trade.price)) return;
        this.tryHeartEntry(symbol);
      }
    });
  }

  private stopHeartFeed(symbol: string) {
    const feed = this.heartFeeds.get(symbol);
    if (!feed) return;
    feed.unsubscribe();
    feed.unsubscribeTrades();
    this.heartFeeds.delete(symbol);
  }

  /** Recompute the level shown in the settings panel without considering an entry. */
  private refreshHeartStatus(symbol: string, candles: Candle[]): void {
    const feed = this.heartFeeds.get(symbol);
    if (!feed) return;

    feed.candleCount = candles.length;
    const latest = candles[candles.length - 1];
    if (!latest) return;

    const needed = requiredCandles(feed.params);
    const state =
      candles.length >= needed
        ? computeHeartSeries(candles, feed.params)[candles.length - 1]
        : null;

    feed.status = {
      interval: feed.interval,
      level: state?.level ?? null,
      isTrendUp: state?.isTrendUp ?? null,
      close: latest.close,
      barTime: latest.time,
      candleCount: candles.length,
      requiredCandles: needed
    };
  }

  /** The heart the strategy is currently trading against, if it is running for this symbol. */
  getHeartStatus(symbol: string): HeartStatus | null {
    return this.heartFeeds.get(symbol)?.status ?? null;
  }

  private evaluateHeart(symbol: string, candles: Candle[]) {
    const feed = this.heartFeeds.get(symbol);
    if (!feed) return;

    const latest = candles[candles.length - 1];
    if (!latest || latest.time <= feed.lastBarTime) return;
    feed.lastBarTime = latest.time;

    this.refreshHeartStatus(symbol, candles);

    // A close through the heart repaints it, and that first trade in the new direction is
    // the one the strategy exists to take, so it is let through unconditionally. The flag
    // survives until an entry actually consumes it: if a position from the previous colour
    // is still open when the heart turns, the trade is owed and gets taken once the account
    // is flat again, however many bars later that is.
    const trendUp = feed.status?.isTrendUp ?? null;
    if (trendUp !== null && feed.lastTrendUp !== null && trendUp !== feed.lastTrendUp) {
      feed.flipEntryPending = true;
    }
    feed.lastTrendUp = trendUp;

    // Replaced on every close, so a setup gets the bar that follows it to be taken out and
    // no longer. Carrying one forward would let a turn from ten bars ago open a position at
    // a price that has nothing to do with it.
    feed.setup = detectReversalSetup(candles);

    this.tryHeartEntry(symbol);
  }

  /**
   * Report whether an AI position is open, so the strategy knows when it is flat and when
   * its cooldown should start. Called by the trading context as the open position list
   * changes; it is idempotent, and only a genuine open-to-flat transition starts the clock.
   */
  setPositionState(symbol: string, isOpen: boolean): void {
    const feed = this.heartFeeds.get(symbol);
    if (!feed || feed.positionOpen === isOpen) return;

    if (feed.positionOpen && !isOpen) {
      feed.cooldownUntil = Date.now() + HEART_REENTRY_COOLDOWN_MS;
    }
    feed.positionOpen = isOpen;
  }

  /**
   * Enter in whichever direction the heart currently points, whenever the strategy is flat
   * and out of its cooldown.
   *
   * The heart is a standing bias, not an event, so this runs continuously rather than only
   * on the bar that flips it: after a position closes and the pause elapses, the same
   * colour opens the same side again. The level itself only moves when a bar closes, so
   * polling in between costs nothing beyond re-reading the last computed state.
   *
   * The heart gives the direction. What gives the timing depends on whether this is the
   * first trade of the colour:
   *
   *  - The close that repaints the heart is itself the signal, so that trade goes on
   *    unconditionally, at the market.
   *  - Every re-entry afterwards waits for a three-candle reversal arguing the same side —
   *    three red then a green for a buy, three green then a red for a sell — and then for
   *    price to take out that turning candle's high (buy) or low (sell). The pattern arms
   *    the trade and the break triggers it, so a setup that is never taken out never
   *    becomes a position.
   */
  private tryHeartEntry(symbol: string): void {
    const feed = this.heartFeeds.get(symbol);
    if (!feed || !this.config) return;

    if (feed.positionOpen) return;
    if (this.startupDelays.has(symbol)) return;

    const now = Date.now();
    if (now < feed.cooldownUntil) return;
    if (now - feed.lastEntryRequest < HEART_ENTRY_RETRY_MS) return;

    const side = heartSide(feed.status);
    if (!side || feed.status === null) return;

    // The bar close is up to a whole interval old by the time a cooldown expires, so the
    // order is priced from the live stream when it has something fresher.
    const price = liveChartStream.getLastPrice(symbol) ?? feed.status.close;
    if (!Number.isFinite(price) || price <= 0) return;

    const takingTheFlip = feed.flipEntryPending;
    const setup = feed.setup;

    if (!takingTheFlip) {
      if (!setup || setup.side !== side) return;
      // Only the bar currently being reported counts. Anything older has been superseded by
      // a close that did not repeat the pattern.
      if (setup.barTime !== feed.status.barTime) return;
      // Armed but not yet triggered: the level is still intact, so there is nothing to do
      // beyond waiting. Deliberately no `lastEntryRequest` here — nothing was requested.
      if (!setupTriggered(setup, price)) return;
    }

    feed.lastEntryRequest = now;

    const data = this.symbolData.get(symbol);
    if (data) {
      data.lastSignalTime = now;
    }

    // Both consumed, so the retry loop cannot open a second position off the same bar and
    // the next entry on this colour has to arm and trigger a setup of its own.
    feed.setup = null;
    feed.flipEntryPending = false;

    console.log(
      `♥ HUGE♥HUG ${symbol} ${feed.interval}: ${side.toUpperCase()} — ` +
        `${feed.status.isTrendUp ? 'green' : 'red'} heart at ${feed.status.level?.toFixed(4)}, ` +
        `${takingTheFlip ? 'heart just turned' : `broke ${setup!.level} off a 3-candle turn`}, ` +
        `price ${price}`
    );

    this.config.onSignal(symbol, side, String(price));
  }

  private monitorAllSymbols() {
    // This method can be used to fetch real-time prices for all active symbols
    // For now, we'll use the prices that are updated via updateSymbolPrice
    this.activeSymbols.forEach(symbol => {
      const data = this.symbolData.get(symbol);
      if (data && Date.now() - data.lastUpdate < 10000) { // 10 seconds timeout
        // Price is fresh, continue monitoring
        return;
      } else {
        // Price is stale, we might want to fetch new price or mark as inactive
      }
    });
  }

  private generateAISignal(symbol: string) {
    const data = this.symbolData.get(symbol);
    if (!data) {
      return;
    }

    const strategy = data.strategy || 'supertrend';

    // HUGE♥HUG reads the heart its own candle feed maintains. It used to fall through to
    // the SuperTrend branch below, so the trades it took came from a completely different
    // rule than the one configured in the strategy panel. It also does not depend on
    // `data.price`, which is only fed for whichever symbol the chart is showing.
    if (HEART_STRATEGY_IDS.has(strategy)) {
      this.tryHeartEntry(symbol);
      return;
    }

    if (!data.price) {
      return;
    }

    const price = parseFloat(data.price);

    // Calculate signal based on selected strategy
    let signal: 'buy' | 'sell' | null = null;
    
    switch (strategy) {
      case 'stochastic':
        signal = this.calculateStochasticSignal(symbol, price, data.config);
        break;
      case 'bollingerbands':
        signal = this.calculateBollingerBandsSignal(symbol, price, data.config);
        break;
      case 'rsi':
        signal = this.calculateRSISignal(symbol, price, data.config);
        break;
      default:
        signal = this.calculateSuperTrendSignal(symbol, price);
    }
    
    if (signal && this.config) {
      this.config.onSignal(symbol, signal, data.price);
    }
  }

  private calculateSuperTrendSignal(symbol: string, currentPrice: number): 'buy' | 'sell' | null {
    const data = this.symbolData.get(symbol);
    if (!data) {
      return null;
    }

    // Get or initialize price history for this symbol
    if (!data.priceHistory) {
      data.priceHistory = [];
    }

    // Check minimum timeframe (1 minute = 60000ms)
    const now = Date.now();
    const minTimeframe = 60000; // 1 minute
    if (data.lastSignalTime && (now - data.lastSignalTime) < minTimeframe) {
      return null;
    }

    // Add current price to history
    data.priceHistory.push({
      price: currentPrice,
      timestamp: now
    });

    // Keep only last 50 prices for calculation
    if (data.priceHistory.length > 50) {
      data.priceHistory = data.priceHistory.slice(-50);
    }

    // Need at least 5 prices for SuperTrend calculation
    if (data.priceHistory.length < 5) {
      return null;
    }

    // Calculate ATR (Average True Range)
    const atr = this.calculateATR(data.priceHistory, 3);
    
    if (atr === 0) {
      return null;
    }
    
    // Calculate SuperTrend with direction
    const { superTrend, direction } = this.calculateSuperTrend(data.priceHistory, atr, 1.0);
    
    // Get current and previous direction values
    const currentDirection = direction[direction.length - 1];
    const previousDirection = direction[direction.length - 2];
    
    // Determine signal based on direction change (following Pine Script logic)
    let signal: 'buy' | 'sell' | null = null;
    
    if (currentDirection !== undefined && previousDirection !== undefined) {
      // Buy Signal: Direction changed from bearish (-1) to bullish (1) - ta.change(direction) > 0
      if (previousDirection === -1 && currentDirection === 1) {
        signal = 'buy';
        data.lastSignalTime = now; // Update last signal time
      }
      // Sell Signal: Direction changed from bullish (1) to bearish (-1) - ta.change(direction) < 0
      else if (previousDirection === 1 && currentDirection === -1) {
        signal = 'sell';
        data.lastSignalTime = now; // Update last signal time
      }
    }

    return signal;
  }

  private calculateATR(priceHistory: Array<{price: number, timestamp: number}>, period: number): number {
    if (priceHistory.length < period + 1) return 0;

    let trueRanges: number[] = [];
    
    for (let i = 1; i < priceHistory.length; i++) {
      const current = priceHistory[i].price;
      const previous = priceHistory[i - 1].price;
      
      // For simplicity, we'll use price change as true range
      // In real implementation, you'd use high, low, close prices
      const trueRange = Math.abs(current - previous);
      trueRanges.push(trueRange);
    }

    // Calculate ATR as simple moving average of true ranges
    const recentTrueRanges = trueRanges.slice(-period);
    return recentTrueRanges.reduce((sum, tr) => sum + tr, 0) / recentTrueRanges.length;
  }

  private calculateSuperTrend(priceHistory: Array<{price: number, timestamp: number}>, atr: number, multiplier: number): {superTrend: number[], direction: number[]} {
    const superTrend: number[] = [];
    const direction: number[] = [];
    const upperBand: number[] = [];
    const lowerBand: number[] = [];
    
    for (let i = 0; i < priceHistory.length; i++) {
      const price = priceHistory[i].price;
      
      // Calculate upper and lower bands
      upperBand[i] = price + (atr * multiplier);
      lowerBand[i] = price - (atr * multiplier);
      
      if (i === 0) {
        // First value - use lower band as initial SuperTrend, direction = 1 (bullish)
        superTrend.push(lowerBand[i]);
        direction.push(1);
      } else {
        const previousSuperTrend = superTrend[i - 1];
        const previousDirection = direction[i - 1];
        
        // Calculate current SuperTrend based on previous direction
        let currentSuperTrend: number;
        let currentDirection: number;
        
        if (previousDirection === 1) {
          // Previous trend was bullish
          currentSuperTrend = Math.max(lowerBand[i], previousSuperTrend);
          // Check if price breaks below SuperTrend
          if (price < currentSuperTrend) {
            currentDirection = -1; // Change to bearish
            currentSuperTrend = upperBand[i]; // Use upper band for bearish trend
          } else {
            currentDirection = 1; // Stay bullish
          }
        } else {
          // Previous trend was bearish
          currentSuperTrend = Math.min(upperBand[i], previousSuperTrend);
          // Check if price breaks above SuperTrend
          if (price > currentSuperTrend) {
            currentDirection = 1; // Change to bullish
            currentSuperTrend = lowerBand[i]; // Use lower band for bullish trend
          } else {
            currentDirection = -1; // Stay bearish
          }
        }
        
        superTrend.push(currentSuperTrend);
        direction.push(currentDirection);
      }
    }
    
    return { superTrend, direction };
  }

  private calculateStochasticSignal(symbol: string, currentPrice: number, config: any): 'buy' | 'sell' | null {
    // Simplified Stochastic calculation
    const data = this.symbolData.get(symbol);
    if (!data || !data.priceHistory || data.priceHistory.length < 14) return null;

    const prices = data.priceHistory.slice(-14).map(p => p.price);
    const highest = Math.max(...prices);
    const lowest = Math.min(...prices);
    
    if (highest === lowest) return null;
    
    const k = ((currentPrice - lowest) / (highest - lowest)) * 100;
    const oversoldLevel = config?.oversoldLevel || 20;
    const overboughtLevel = config?.overboughtLevel || 80;
    
    if (k <= oversoldLevel) {
      return 'buy';
    } else if (k >= overboughtLevel) {
      return 'sell';
    }
    
    return null;
  }

  private calculateBollingerBandsSignal(symbol: string, currentPrice: number, config: any): 'buy' | 'sell' | null {
    // Simplified Bollinger Bands calculation
    const data = this.symbolData.get(symbol);
    if (!data || !data.priceHistory || data.priceHistory.length < 20) return null;

    const prices = data.priceHistory.slice(-20).map(p => p.price);
    const sma = prices.reduce((sum, price) => sum + price, 0) / prices.length;
    
    // Calculate standard deviation
    const variance = prices.reduce((sum, price) => sum + Math.pow(price - sma, 2), 0) / prices.length;
    const stdDev = Math.sqrt(variance);
    
    const multiplier = config?.multiplier || 2;
    const upperBand = sma + (stdDev * multiplier);
    const lowerBand = sma - (stdDev * multiplier);
    
    if (currentPrice <= lowerBand) {
      return 'buy'; // Price touched lower band - oversold
    } else if (currentPrice >= upperBand) {
      return 'sell'; // Price touched upper band - overbought
    }
    
    return null;
  }

  private calculateRSISignal(symbol: string, currentPrice: number, config: any): 'buy' | 'sell' | null {
    // Simplified RSI calculation
    const data = this.symbolData.get(symbol);
    if (!data || !data.priceHistory || data.priceHistory.length < 14) return null;

    const prices = data.priceHistory.slice(-14).map(p => p.price);
    let gains = 0;
    let losses = 0;
    
    for (let i = 1; i < prices.length; i++) {
      const change = prices[i] - prices[i - 1];
      if (change > 0) {
        gains += change;
      } else {
        losses += Math.abs(change);
      }
    }
    
    const avgGain = gains / 13;
    const avgLoss = losses / 13;
    
    if (avgLoss === 0) return null;
    
    const rs = avgGain / avgLoss;
    const rsi = 100 - (100 / (1 + rs));
    
    const oversoldLevel = config?.oversoldLevel || 30;
    const overboughtLevel = config?.overboughtLevel || 70;
    
    if (rsi <= oversoldLevel) {
      return 'buy';
    } else if (rsi >= overboughtLevel) {
      return 'sell';
    }
    
    return null;
  }

  getActiveSymbols(): string[] {
    return Array.from(this.activeSymbols);
  }

  getSymbolData(symbol: string): AISymbolData | undefined {
    return this.symbolData.get(symbol);
  }

  isSymbolActive(symbol: string): boolean {
    const isActive = this.activeSymbols.has(symbol);
    // console.log('AI Trading Service: isSymbolActive for', symbol, ':', isActive, 'Active symbols:', Array.from(this.activeSymbols));
    return isActive;
  }

  isSymbolInStartupDelay(symbol: string): boolean {
    return this.startupDelays.has(symbol);
  }

  getStartupDelayRemaining(symbol: string): number {
    const delay = this.startupDelays.get(symbol);
    if (!delay) return 0;
    
    // Calculate remaining time (approximate)
    // Note: This is an approximation since we can't get exact remaining time from setTimeout
    return this.AI_STARTUP_DELAY;
  }

  // Clear signal for a symbol (useful after trade execution)
  clearSignal(symbol: string) {
    const data = this.symbolData.get(symbol);
    if (data) {
      // Update the data to clear any cached signal state
      this.symbolData.set(symbol, {
        ...data,
        lastSignalTime: undefined
      });
      
      // Note: We don't call onSignal with null as it expects 'buy' | 'sell'
      // The signal clearing is handled by the context layer
    }
  }

  cleanup() {
    // Clear global monitoring interval
    if (this.globalMonitoringInterval) {
      clearInterval(this.globalMonitoringInterval);
      this.globalMonitoringInterval = null;
    }
    
    // Clear all symbol-specific intervals
    this.intervals.forEach((interval, symbol) => {
      clearInterval(interval);
    });
    this.intervals.clear();
    
    // Clear all startup delays
    this.startupDelays.forEach((delay, symbol) => {
      clearTimeout(delay);
    });
    this.startupDelays.clear();

    this.heartFeeds.forEach(feed => feed.unsubscribe());
    this.heartFeeds.clear();
    
    this.activeSymbols.clear();
    this.symbolData.clear();
    this.isRunning = false;
  }
}

// Export singleton instance
export const aiTradingService = new AITradingService();
